import { Link } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ReviewsSection from '@/pages/home/components/ReviewsSection';

const PHILOSOPHY = [
  { icon: 'ri-rocket-line', color: 'bg-primary-500', title: '每日更新不停歇', desc: '以更新速度为核心，最新上线韩漫新作第一时间收录，连载进度实时同步，让追更不再等待。' },
  { icon: 'ri-archive-line', color: 'bg-accent-500', title: '收录齐全分类清晰', desc: '奇幻玄幻、恋爱浪漫、热血动作、悬疑惊悚等十大赛道，分类清晰，找漫画又快又准。' },
  { icon: 'ri-eye-line', color: 'bg-secondary-500', title: '进度同步一目了然', desc: '连载中、已完结、即将上线三种状态清晰标注，本周更新按日整理，追更进度了然于心。' },
  { icon: 'ri-heart-line', color: 'bg-accent-600', title: '以追漫人为中心', desc: '站在读者视角做内容，推荐真实可追的高分韩漫，拒绝标题党与空洞内容。' },
];

const PRINCIPLES = [
  '收录真实在更的韩漫作品，标注清晰、进度同步',
  '每日更新不停歇，保证最新内容第一时间可见',
  '分类与标签准确，帮助读者快速锁定想看的内容',
  '推荐基于真实人气与评分，不做夸张的标题党',
];

export default function About() {
  return (
    <>
      <Seo
        title="关于我们 - 韩国漫画大全 · 每日更新 · 连载进度同步"
        description="了解韩国漫画大全：品牌介绍、网站定位、发展理念与内容原则。做每日更新不停歇、连载进度同步的韩漫追更第一站。"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于韩国漫画大全',
            inLanguage: 'zh-CN',
            description: '韩国漫画大全是一个每日更新、连载进度同步的韩国漫画内容门户。',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '韩国漫画大全',
            url: 'https://kdramplet.github.io/',
          },
        ]}
      />
      <Navbar />

      <main className="mx-auto max-w-5xl px-4 pb-16 pt-24 md:px-6 md:pb-24 md:pt-32">
        <nav aria-label="面包屑" className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-800">关于我们</span>
        </nav>

        {/* 品牌介绍 */}
        <div className="mb-14">
          <span className="text-xs font-medium tracking-wide text-primary-600">ABOUT US</span>
          <h1 className="mt-1 font-heading text-3xl text-foreground-950 md:text-4xl">关于韩国漫画大全</h1>
          <p className="mt-4 max-w-3xl text-sm leading-loose text-foreground-700 md:text-base md:leading-loose">
            韩国漫画大全是一个专注韩国漫画的内容门户，以<b>每日更新不停歇</b>和<b>连载进度同步</b>为核心，为追漫人提供最新、最全、最及时的韩漫资讯。
            从最新上线韩漫新作的第一时间收录，到本周更新的按日汇总，再到连载中、已完结、即将上线的清晰标注，
            我们希望每一个想看韩漫大全最新内容的你，都能在这里快速找到心仪的作品，追更路上不停歇。
          </p>
        </div>

        {/* 定位 */}
        <section className="mb-14">
          <h2 className="mb-6 font-heading text-2xl text-foreground-950">网站定位</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              { num: '01', title: '更新最快', desc: '每日更新不停歇，新作上线第一时间收录' },
              { num: '02', title: '进度最清', desc: '连载进度同步，本周更新一目了然' },
              { num: '03', title: '找漫最准', desc: '分类清晰、推荐真实，快速锁定想看内容' },
            ].map((s) => (
              <div key={s.num} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="font-heading text-3xl text-primary-300">{s.num}</span>
                <h3 className="mt-2 text-base font-semibold text-foreground-900">{s.title}</h3>
                <p className="mt-1 text-sm text-foreground-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 发展理念 */}
        <section className="mb-14">
          <h2 className="mb-6 font-heading text-2xl text-foreground-950">发展理念</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {PHILOSOPHY.map((p) => (
              <div key={p.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white ${p.color}`}>
                  <i className={`${p.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="text-base font-semibold text-foreground-900">{p.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 内容原则 */}
        <section className="mb-14">
          <h2 className="mb-6 font-heading text-2xl text-foreground-950">内容原则</h2>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <ul className="space-y-3">
              {PRINCIPLES.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-500 text-white">
                    <i className="ri-check-line text-xs"></i>
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <ReviewsSection />

        {/* 联系方式 */}
        <section className="rounded-lg bg-background-100/70 p-6 md:p-8">
          <h2 className="mb-4 font-heading text-2xl text-foreground-950">联系我们</h2>
          <p className="mb-6 text-sm text-foreground-600">对内容有建议，或想反馈追更问题，欢迎随时联系我们。</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-white"><i className="ri-mail-line text-lg"></i></span>
              <div>
                <p className="text-sm font-medium text-foreground-900">邮箱</p>
                <p className="text-sm text-foreground-600">contact@kr-manhwa.example.com</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-500 text-white"><i className="ri-time-line text-lg"></i></span>
              <div>
                <p className="text-sm font-medium text-foreground-900">服务时间</p>
                <p className="text-sm text-foreground-600">每日更新，全年无休</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}